import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, EMPTY } from 'rxjs';
import { map, catchError, takeUntil } from 'rxjs/operators';
import { RxStomp } from '@stomp/rx-stomp';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { NotificacionResponse } from '../models/notificacion.model';
import { EscuadronResponse } from '../models/dashboard.model';
import { InsigniaResponse } from '../models/insignia.model';

@Injectable({ providedIn: 'root' })
export class SelfSpaceWebSocketService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private rxStomp: RxStomp | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly isConnectedSubject = new BehaviorSubject<boolean>(false);
  readonly isConnected$ = this.isConnectedSubject.asObservable();

  // Cache subscriptions for idempotency
  private readonly subscriptionCache = new Map<string, Observable<any>>();

  private static readonly BACKOFF_DELAYS = [5000, 10000, 20000, 40000, 60000];

  constructor(private readonly keycloakService: KeycloakService) {}

  async connect(): Promise<void> {
    if (this.rxStomp) return;

    try {
      const token = await this.keycloakService.getToken();
      const wsUrl = environment.backEndHost.replace(/^http/, 'ws') + '/websocket/tracker?access_token=' + token;

      this.rxStomp = new RxStomp();
      this.rxStomp.configure({
        brokerURL: wsUrl,
        reconnectDelay: 0, // We handle reconnection manually
        heartbeatIncoming: 20000,
        heartbeatOutgoing: 20000,
        debug: (msg: string) => {
          if (!environment.production) {
            console.debug('[WS]', msg);
          }
        }
      });

      this.rxStomp.connected$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.isConnectedSubject.next(true);
          this.reconnectAttempt = 0;
          this.subscriptionCache.clear();
        });

      this.rxStomp.stompErrors$
        .pipe(takeUntil(this.destroy$))
        .subscribe(frame => {
          console.error('[WS] STOMP error:', frame);
        });

      this.rxStomp.webSocketErrors$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.isConnectedSubject.next(false);
          this.scheduleReconnect();
        });

      this.rxStomp.activate();
    } catch (error) {
      console.error('[WS] Connection error:', error);
      this.scheduleReconnect();
    }
  }

  private async scheduleReconnect(): Promise<void> {
    if (this.reconnectTimer) return;

    const delay = SelfSpaceWebSocketService.BACKOFF_DELAYS[
      Math.min(this.reconnectAttempt, SelfSpaceWebSocketService.BACKOFF_DELAYS.length - 1)
    ];
    this.reconnectAttempt++;

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      try {
        // Refresh token before reconnecting
        await this.keycloakService.updateToken(30);
        this.disconnect();
        await this.connect();
      } catch (error) {
        console.error('[WS] Reconnect failed:', error);
        this.scheduleReconnect();
      }
    }, delay);
  }

  // === Topic Subscriptions (idempotent) ===

  watchNotificaciones(email: string): Observable<NotificacionResponse> {
    const topicEmail = (email || '').toLowerCase();
    const topic = `/topic/self-space/notificaciones/${topicEmail}`;
    return this.getOrCreateSubscription<NotificacionResponse>(topic);
  }

  watchEscuadron(equipoId: number): Observable<EscuadronResponse> {
    const topic = `/topic/self-space/escuadron/${equipoId}`;
    return this.getOrCreateSubscription<EscuadronResponse>(topic);
  }

  watchInsignias(email: string): Observable<Partial<InsigniaResponse>> {
    const topicEmail = (email || '').toLowerCase();
    const topic = `/topic/self-space/insignias/${topicEmail}`;
    return this.getOrCreateSubscription<Partial<InsigniaResponse>>(topic);
  }

  // === Client-to-Server ===

  sendAnimarEquipo(email: string, mensaje: string): void {
    if (!this.rxStomp) return;
    this.rxStomp.publish({
      destination: '/app/self-space/escuadron/animar',
      body: JSON.stringify({ email, mensaje })
    });
  }

  // === Helpers ===

  private getOrCreateSubscription<T>(topic: string): Observable<T> {
    if (this.subscriptionCache.has(topic)) {
      return this.subscriptionCache.get(topic)!;
    }

    if (!this.rxStomp) {
      return EMPTY;
    }

    const obs$ = this.rxStomp.watch(topic).pipe(
      map(message => {
        try {
          return JSON.parse(message.body) as T;
        } catch (e) {
          console.error(`[WS] JSON parse error on ${topic}:`, e);
          throw e;
        }
      }),
      catchError((err, caught) => {
        // Re-subscribe on parse error — don't break the stream
        console.warn(`[WS] Re-subscribing to ${topic} after error`);
        return caught;
      }),
      takeUntil(this.destroy$)
    );

    this.subscriptionCache.set(topic, obs$);
    return obs$;
  }

  disconnect(): void {
    if (this.rxStomp) {
      this.rxStomp.deactivate();
      this.rxStomp = null;
    }
    this.subscriptionCache.clear();
    this.isConnectedSubject.next(false);
  }

  ngOnDestroy(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
